import { PencilIcon, XIcon } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import { Button } from '@rocco/components/button';
import {
    Drawer,
    DrawerContent,
    DrawerDescription,
    DrawerHeader,
    DrawerTitle,
    DrawerTrigger,
} from '@rocco/components/drawer';
import type { MeUser } from '~/server/types';
import type { PartialConversation } from '../../types';
import { DRAWER_WIDTH_REM, useChat } from '../../hooks/use-chat';
import { ParticipantAvatar } from '../ParticipantAvatar';
import { ChatInboxFloatingButton } from './ChatInboxFloatingButton';
import { ChatInboxList } from './ChatInboxList';

/**
 * The floating chat inbox bubble and the chat inbox drawer.
 */
export function ChatInboxDrawer({
    children,
    className,
    conversations,
    user,
}: {
    children?: React.ReactNode;
    className?: string;
    /**
     * The user's list of conversations. A `null` value means that the conversations
     * are not yet fetched.
     */
    conversations: PartialConversation[] | null;
    /**
     * The logged-in user.
     */
    user: MeUser;
}) {
    const { chatOpenState, setChatOpenState, openChatBox } = useChat();

    return (
        <Drawer
            open={chatOpenState === 'open'}
            direction="right"
            modal={false}
            dismissible={false}
            repositionInputs={false}
            onOpenChange={(open) => setChatOpenState(open ? 'open' : 'closed')}
        >
            <DrawerTrigger asChild>
                <ChatInboxFloatingButton className={className} />
            </DrawerTrigger>
            {/* Make the `DrawerContent` take up the whole screen, render the drawer
            on the right and the children (chat boxes) to the left of the drawer. */}
            <DrawerContent
                className={cn(
                    // Make the `DrawerContent` full screen.
                    'data-[vaul-drawer-direction=right]:inset-0 data-[vaul-drawer-direction=right]:w-unset data-[vaul-drawer-direction=right]:sm:max-w-unset',
                    // Disable the default Drawer slide-in animation, implement custom animation.
                    'animate-none transition-none',
                    'bg-transparent'
                )}
                noHandle
                hideDefaultCloseButton
            >
                <div className="relative flex h-full w-full items-end overflow-hidden">
                    {/* Fake overlay to close drawer on click */}
                    <div
                        className="absolute inset-0 bg-black/30 duration-500 animate-in fade-in"
                        onClick={() => setChatOpenState('closed')}
                    ></div>
                    {/* Need to place drawer content and children z-index >= 0 to
                    prevent clicking the overlay */}
                    {children}
                    <div
                        className="z-1 ml-auto flex h-full shrink-0 flex-col bg-background duration-500 animate-in slide-in-from-bottom"
                        style={{ width: `${DRAWER_WIDTH_REM}rem` }}
                    >
                        {/* Suppress missing title and description console warning */}
                        <DrawerHeader className="hidden" aria-hidden>
                            <DrawerTitle className="hidden" aria-hidden>
                                Messages
                            </DrawerTitle>
                            <DrawerDescription className="hidden" aria-hidden />
                        </DrawerHeader>
                        {/* Header */}
                        <div className="relative flex select-text items-center gap-2 py-8 pl-4 pr-16">
                            <ParticipantAvatar
                                size="square56"
                                participant={user}
                            />
                            <div className="min-w-0 flex-1 space-y-0.5">
                                <div className="text-lg text-foreground">
                                    {user.Name}
                                </div>
                                <div className="truncate text-sm text-muted-foreground">
                                    {user.Email}
                                </div>
                            </div>
                            <button
                                className="absolute right-6 top-6 p-1"
                                aria-label="Close"
                                onClick={() => setChatOpenState('closed')}
                            >
                                <XIcon className="size-6 opacity-70 hover:opacity-100" />
                            </button>
                        </div>
                        {/* Inbox list */}
                        <ChatInboxList
                            className="min-h-0 flex-1 px-4 py-2"
                            conversations={conversations ?? []}
                            isLoading={!conversations}
                        />
                        {/* New message button */}
                        <div className="flex items-center justify-center px-2 py-8">
                            <Button
                                className="flex items-center gap-2"
                                color="dark"
                                size="larger"
                                onClick={() => openChatBox(0)}
                            >
                                New Message
                                <PencilIcon className="size-4" />
                            </Button>
                        </div>
                    </div>
                </div>
            </DrawerContent>
        </Drawer>
    );
}
